// https://www.annuities.pacificlife.com/enroll-ui/myteamsassist
import React, { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { QuestionCircle } from "react-bootstrap-icons";
import ReactTooltip from "react-tooltip";
import Toast from "react-bootstrap/Toast";
import axios from "axios";
import {
    REQUIRED_FIELDS,
    ADVISOR_ASSISTANT,
    ANNUITIES_FP_LOGIN_PAGE_URL_PATH,
    REGISTRATION_SUCCESSFUL_URL_PATH
} from "./constants";
import {
    emailValidation,
    emailVerifyMatch,
    userNameValidation
} from "./validation";
import { analytics } from "./analytics";
import { Header, Footer } from "./components";

const MyTeamsAssistantReg = () => {
    const [user, setUser] = useState({
        firstName: "",
        lastName: "",
        email: "",
        verifyEmail: "",
        fpFirstName: "",
        fpLastName: "",
        userName: ""
    });
    const [errors, setErrors] = useState({
        email: "",
        verifyEmail: ""
    });
    const [toastMessage, setToastMessage] = useState("");
    const [submitDisabled, setSubmitDisabled] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const ASSISTANT_ENROLL_ENDPOINT = "/enroll/annuities/assitant/register";

    useEffect(() => {
        analytics();
    }, []);

    useEffect(() => {
        const requiredFields = REQUIRED_FIELDS[ADVISOR_ASSISTANT];
        for (const element of requiredFields) {
            const fieldName = element;
            if (!user[fieldName]) {
                setSubmitDisabled(true);
                return;
            }
        }
        // check if there is any error
        const errorFields = Object.keys(errors);
        for (const element of errorFields) {
            const fieldName = element;
            if (errors[fieldName] !== "") {
                setSubmitDisabled(true);
                return;
            }
        }
        setSubmitDisabled(false);
    }, [user, errors]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUser((prev) => ({ ...prev, [name]: value }));
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        let errorMessage = "";

        if (name === "email") {
            const emailValid = emailValidation(value);
            errorMessage = emailValid ? "" : "Enter valid email";
        }

        if (name === "verifyEmail") {
            errorMessage = emailVerifyMatch(user.email, value)
                ? ""
                : "Email addresses do not match";
        }

        if (name === "userName") {
            const valid = userNameValidation(value);
            const tooShortOrLong = value.length < 6 || value.length > 8;
            const hasSpecialCharOrWhiteSpace = /\W|\D/g.test(value);
            if (valid) {
                errorMessage = "";
            } else if (tooShortOrLong) {
                errorMessage =
                    "Username must be between 6 and 8 characters long";
            } else if (hasSpecialCharOrWhiteSpace) {
                errorMessage =
                    "Username must not contain spaces or special characters";
            }
        }

        setErrors((prev) => ({ ...prev, [name]: errorMessage }));
    };

    const handleCancel = () => {
        document.location.href = ANNUITIES_FP_LOGIN_PAGE_URL_PATH;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            firstName: user.firstName,
            lastName: user.lastName,
            fpFirstName: user.fpFirstName,
            fpLastName: user.fpLastName,
            email: user.email.trim(),
            id: user.userName
        };
        setSubmitting(true);
        try {
            await axios({
                url: ASSISTANT_ENROLL_ENDPOINT,
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                data: JSON.stringify(payload)
            });
            document.location.href = REGISTRATION_SUCCESSFUL_URL_PATH;
        } catch (error) {
            setToastMessage(
                error.response.data.errorMessage ||
                    "Something went wrong, please try again later."
            );
        }
        setSubmitting(false);
    };

    const handleClose = () => {
        setToastMessage("");
    };

    return (
        <>
            <Header />
            <div className="mainDiv myteams">
                <div className="content">
                    {
                        <div id="ext">
                            <div className="lt-col">
                                <h3 className="heading">
                                    Create a Team Member Account
                                </h3>
                                <div className="subHeading">
                                    Register to view supporting product
                                    information, manage individual client
                                    contracts, and get access to support.
                                </div>
                            </div>
                        </div>
                    }
                    <Container className="mainContainer fluid">
                        <Form>
                            <Row>
                                <Col md="5">
                                    <Form.Label>First Name</Form.Label>
                                    <Form.Control
                                        name="firstName"
                                        value={user.firstName}
                                        onChange={handleChange}
                                        maxLength={25}
                                    />
                                </Col>
                                <Col></Col>
                                <Col md="5">
                                    <Form.Label>Last Name</Form.Label>
                                    <Form.Control
                                        name="lastName"
                                        value={user.lastName}
                                        onChange={handleChange}
                                        maxLength={35}
                                    />
                                </Col>
                                <Col></Col>
                            </Row>
                            <Row>
                                <Col md="5">
                                    <ReactTooltip />
                                    <Form.Label className="emailClass">
                                        Email
                                        <QuestionCircle
                                            className="iconClass"
                                            data-tip="Add your full email address associated with your firm."
                                        />
                                    </Form.Label>
                                    <Form.Control
                                        name="email"
                                        value={user.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        maxLength={60}
                                    />
                                    <span className="validation-error-message">
                                        {errors.email}
                                    </span>
                                </Col>
                                <Col></Col>
                                <Col md="5">
                                    <Form.Label>Verify Email</Form.Label>
                                    <Form.Control
                                        name="verifyEmail"
                                        value={user.verifyEmail}
                                        onChange={handleChange}
                                        onPaste={(e) => e.preventDefault()}
                                        onBlur={handleBlur}
                                        maxLength={60}
                                    />
                                    <span className="validation-error-message">
                                        {errors.verifyEmail}
                                    </span>
                                </Col>
                                <Col></Col>
                            </Row>
                            <Row>
                                <Col md="5">
                                    <Form.Label>Advisor First Name</Form.Label>
                                    <Form.Control
                                        name="fpFirstName"
                                        value={user.fpFirstName}
                                        onChange={handleChange}
                                        maxLength={25}
                                    />
                                </Col>
                                <Col></Col>
                                <Col md="5">
                                    <Form.Label>Advisor Last Name</Form.Label>
                                    <Form.Control
                                        name="fpLastName"
                                        value={user.fpLastName}
                                        onChange={handleChange}
                                        maxLength={35}
                                    />
                                </Col>
                                <Col></Col>
                            </Row>
                            <Row>
                                <Col md="5">
                                    <ReactTooltip
                                        multiline
                                        id="username-tooltip"
                                    />
                                    <Form.Label className="createUsername">
                                        Create Username
                                        <QuestionCircle
                                            className="iconClassName"
                                            data-for="username-tooltip"
                                            data-tip="Username must be 6-8 characters long,<br/> may contain letters and numbers,<br/> no spaces or special characters."
                                        />
                                    </Form.Label>
                                    <div className="sublabel">
                                        Cannot already be in use with Pacific
                                        Life
                                    </div>
                                    <Form.Control
                                        name="userName"
                                        value={user.userName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        maxLength={8}
                                    />
                                    <span className="validation-error-message">
                                        {errors.userName}
                                    </span>
                                </Col>
                                <Col></Col>
                            </Row>
                        </Form>
                        <Row>
                            <Col md="4"></Col>
                            <Col md="4">
                                <div className="buttons">
                                    <Button
                                        variant="secondary"
                                        type="cancel"
                                        onClick={handleCancel}
                                        onKeyPress={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                            }
                                        }}
                                        data-track="analytics"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        onClick={handleSubmit}
                                        disabled={submitDisabled || submitting}
                                        data-track="analytics"
                                    >
                                        {submitting
                                            ? "Registering"
                                            : "Register"}
                                    </Button>
                                </div>
                            </Col>
                            <Col></Col>
                        </Row>
                        <Row>
                            <Col>
                                {
                                    <div
                                        id="regAccessFormMsg"
                                        className="myteams-ftr"
                                    >
                                        Please note that the information
                                        submitted must match what was provided
                                        by the Financial Professional.
                                        <div id="regAcess">
                                            <a
                                                href={
                                                    ANNUITIES_FP_LOGIN_PAGE_URL_PATH
                                                }
                                            >
                                                Already have an account?
                                            </a>
                                        </div>
                                    </div>
                                }
                            </Col>
                        </Row>
                    </Container>
                </div>
                {toastMessage !== "" && (
                    <Toast onClose={handleClose} className="toast-cont">
                        <Toast.Header>
                            <strong className="me-auto">{toastMessage}</strong>
                        </Toast.Header>
                    </Toast>
                )}
            </div>
            <Footer />
        </>
    );
};

export default MyTeamsAssistantReg;
